import React, { useState } from 'react';
import { Popover, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

// ── Score Ring ────────────────────────────────────────────────────────────────
function ScoreRing({ score, size = 64 }) {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const fill = circ * (1 - (score || 0) / 100);
  const color = score >= 70 ? '#16a34a' : score >= 40 ? '#f59e0b' : '#dc2626';
  return (
    <svg width={size} height={size} style={{ flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={6} />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke={color} strokeWidth={6}
        strokeDasharray={circ} strokeDashoffset={fill}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle"
        fontSize={size * 0.28} fontWeight={700} fill={color}>
        {score ?? '—'}
      </text>
    </svg>
  );
}

// ── Compute overall score from analysis data ──────────────────────────────────
export function computeOverallScore(ca) {
  if (!ca) return null;
  const mechScore = ca.mechanics?.score ?? 0;
  const spamScore = ca.spam?.score ?? 0;
  const personalScore = ((ca.personalization?.depthLevel ?? 1) / 4) * 100;
  const coherenceScore = ca.coherence?.score ?? 0;
  const parts = [mechScore, spamScore, personalScore];
  if (ca.coherence) parts.push(coherenceScore);
  return Math.round(parts.reduce((a, b) => a + b, 0) / parts.length);
}

// ── Build checks list from analysis data ─────────────────────────────────────
function buildChecks(ca, stepNumber) {
  if (!ca) return [];
  const checks = [];

  // Spam
  const triggers = ca.spam?.triggers ?? [];
  const spamScore = ca.spam?.score ?? 100;
  if (triggers.length > 0) {
    const phrases = triggers.slice(0, 3).map(t => `"${typeof t === 'string' ? t : t.phrase}"`).join(', ');
    checks.push({
      icon: '🛡',
      label: `${triggers.length} spam word${triggers.length > 1 ? 's' : ''} found — emails may go to junk`,
      status: 'fix',
      detail: `Words like ${phrases} trigger spam filters in Gmail and Outlook.`,
      subDetail: 'Even with perfect DNS setup, spam words in the email body can land you in the junk folder. Each flagged word reduces deliverability.',
    });
  } else if (spamScore >= 80) {
    checks.push({
      icon: '🛡',
      label: 'No spam words detected',
      status: 'good',
      detail: 'Clean copy — should pass content filters without issues.',
    });
  }

  // Personalization
  const depth = ca.personalization?.depthLevel ?? 1;
  const signals = ca.personalization?.signals ?? {};
  const missingSignals = ['firstName', 'companyName', 'industry', 'roleTitle', 'recentTrigger']
    .filter(k => !signals[k]);
  if (depth <= 1) {
    checks.push({
      icon: '🎯',
      label: 'Make it more personal',
      status: 'improve',
      detail: 'You only used their first name. Mention their company, role, or something specific about them.',
      subDetail: 'Emails with company name + role context get 2–3x more replies than name-only personalization.',
    });
  } else if (depth === 2) {
    checks.push({
      icon: '🎯',
      label: 'Personalization could be stronger',
      status: 'improve',
      detail: missingSignals.length ? `Try adding: ${missingSignals.slice(0, 2).join(', ')}.` : 'Add industry or role-specific context.',
    });
  } else {
    checks.push({
      icon: '🎯',
      label: 'Personalized to their business',
      status: 'good',
      detail: `L${depth} personalization — strong context signals detected.`,
    });
  }

  // Coherence (Step 2+)
  if (stepNumber > 1 && ca.coherence) {
    const { score: cScore = 0, notes = [] } = ca.coherence;
    const badNotes = notes.filter(n => (typeof n === 'string' ? false : n.status === 'bad'));
    if (badNotes.length > 0) {
      checks.push({
        icon: '🔗',
        label: badNotes[0]?.label || 'This email repeats Step 1 without adding new value',
        status: 'fix',
        detail: badNotes[0]?.detail || 'Follow-ups that feel like reminders get ignored. Add something new — a stat, case study, or insight.',
        subDetail: 'The best Step 2 emails that add new information outperform reminders by 40%.',
      });
    } else if (cScore >= 70) {
      checks.push({
        icon: '🔗',
        label: 'Builds naturally on the previous emails',
        status: 'good',
        detail: `Coherence score ${cScore}/100 — good step-to-step flow.`,
      });
    }
  }

  // Reading level
  const rl = ca.mechanics?.readingLevel;
  if (rl) {
    const isHigh = rl.value && parseInt(rl.value) > (parseInt(rl.idealMax) || 8);
    checks.push({
      icon: '📖',
      label: isHigh ? 'Reading level is a bit high' : 'Reading level is good',
      status: isHigh ? 'improve' : 'good',
      detail: isHigh
        ? `${rl.value} reading level. Simpler language improves reply rates.`
        : `${rl.value} — clear and easy to read.`,
      subDetail: isHigh ? (rl.suggestion || 'Aim for Grade 6–8 for cold outreach.') : undefined,
    });
  }

  // Word count / email length
  const wc = ca.mechanics?.wordCount;
  if (wc) {
    const inRange = wc.value >= wc.idealMin && wc.value <= wc.idealMax;
    checks.push({
      icon: '✏️',
      label: inRange ? 'Email length is perfect' : wc.value < wc.idealMin ? 'Email is too short' : 'Email is too long',
      status: inRange ? 'good' : 'improve',
      detail: inRange
        ? `${wc.value} words — right in the sweet spot for cold outreach.`
        : wc.suggestion || `Aim for ${wc.idealMin}–${wc.idealMax} words.`,
      subDetail: inRange ? `Emails between ${wc.idealMin}–${wc.idealMax} words get the highest reply rates.` : undefined,
    });
  }

  // CTA
  const cta = ca.mechanics?.ctaCount;
  if (cta) {
    const ctaOk = cta.value === cta.ideal;
    checks.push({
      icon: '💬',
      label: ctaOk ? 'Strong call-to-action' : 'Call-to-action needs work',
      status: ctaOk ? 'good' : 'improve',
      detail: cta.suggestion || (ctaOk ? 'Ends with a clear question.' : `Ideal: ${cta.ideal} CTA.`),
      subDetail: ctaOk ? 'A single, clear question gets 21% more replies than multiple asks.' : undefined,
    });
  }

  // Subject line
  const sl = ca.mechanics?.subjectLength;
  if (sl) {
    const slOk = sl.value >= sl.idealMin && sl.value <= sl.idealMax;
    checks.push({
      icon: '📨',
      label: slOk ? 'Subject line looks good' : 'Subject line needs work',
      status: slOk ? 'good' : 'improve',
      detail: slOk
        ? `${sl.value} chars — works well on both mobile and desktop.`
        : sl.suggestion || `Aim for ${sl.idealMin}–${sl.idealMax} characters.`,
    });
  }

  return checks;
}

// ── Check Row ─────────────────────────────────────────────────────────────────
const STATUS_BADGE = {
  fix:     { label: 'Fix this',    cls: 'seq-iq-badge-fix' },
  improve: { label: 'Can Improve', cls: 'seq-iq-badge-improve' },
  good:    { label: 'Good',        cls: 'seq-iq-badge-good' },
};

function CheckRow({ check, isOpen, onToggle }) {
  const badge = STATUS_BADGE[check.status] || STATUS_BADGE.good;
  return (
    <div className={`seq-iq-check-row ${check.status} ${isOpen ? 'expanded' : ''}`} onClick={onToggle}>
      <div className="seq-iq-check-row-top">
        <span className="seq-iq-check-icon">{check.icon}</span>
        <span className="seq-iq-check-label">{check.label}</span>
        <span className={`seq-iq-badge ${badge.cls}`}>{badge.label}</span>
        <span className="seq-iq-check-arrow">
          {isOpen ? <KeyboardArrowUpIcon style={{ fontSize: 16 }} /> : <KeyboardArrowDownIcon style={{ fontSize: 16 }} />}
        </span>
      </div>
      {isOpen && (
        <div className="seq-iq-check-detail">
          <div>{check.detail}</div>
          {check.subDetail && <div className="seq-iq-check-subdetail">{check.subDetail}</div>}
        </div>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function SequenceInsightsPopup({ anchorEl, onClose, contentAnalysis, analyzing, autoImproving, isAIMode, stepNumber = 1, stepName, onAnalyze, onAutoImprove }) {
  const open = Boolean(anchorEl);
  const [openIndex, setOpenIndex] = useState(null);
  const handleToggle = (i) => setOpenIndex(prev => prev === i ? null : i);
  const score = computeOverallScore(contentAnalysis);
  const replyRate = contentAnalysis?.replyRate?.predicted;
  const checks = buildChecks(contentAnalysis, stepNumber);

  const fixCount = checks.filter(c => c.status === 'fix').length;
  const improveCount = checks.filter(c => c.status === 'improve').length;
  const goodCount = checks.filter(c => c.status === 'good').length;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      className="seq-insights-popover"
      PaperProps={{ className: 'seq-iq-popup' }}
    >
      {/* Header */}
      <div className="seq-iq-header">
        <div className="seq-iq-header-left">
          <ScoreRing score={score} size={64} />
          <div className="seq-iq-header-meta">
            <div className="seq-iq-header-title">Email Quality Score</div>
            <div className="seq-iq-header-rate">
              Estimated reply rate: <strong>{replyRate != null ? `${replyRate}%` : '—'}</strong>
              <span className="seq-iq-header-avg"> avg 3.4%</span>
            </div>
            <div className="seq-iq-chips">
              {fixCount > 0 && <span className="seq-iq-chip fix">● {fixCount} to fix</span>}
              {improveCount > 0 && <span className="seq-iq-chip improve">● {improveCount} to improve</span>}
              {goodCount > 0 && <span className="seq-iq-chip good">● {goodCount} good</span>}
            </div>
          </div>
        </div>
        <button className="seq-insights-close" onClick={onClose}><CloseIcon style={{ fontSize: 18 }} /></button>
      </div>

      {/* Body */}
      <div className="seq-iq-body">
        {analyzing ? (
          <div className="seq-analysis-loading">
            <CircularProgress size={22} />
            <span>Analyzing…</span>
          </div>
        ) : checks.length === 0 ? (
          <div className="seq-insights-placeholder">Click Analyze to see email quality insights.</div>
        ) : (
          checks.map((c, i) => <CheckRow key={i} check={c} isOpen={openIndex === i} onToggle={() => handleToggle(i)} />)
        )}
      </div>

      {/* Footer */}
      <div className="seq-iq-footer">
        <button className="seq-insights-btn-regenerate" onClick={onAnalyze} disabled={analyzing}>
          <RefreshIcon style={{ fontSize: 15, marginRight: 4 }} /> Regenerate
        </button>
        {isAIMode && (
          fixCount > 0 ? (
            <button className="seq-iq-btn-fix" onClick={onAutoImprove} disabled={analyzing || autoImproving}>
              {autoImproving
                ? <><CircularProgress size={11} style={{ color: 'inherit', marginRight: 4 }} /> Fixing…</>
                : <>✦ Fix {fixCount} Issue{fixCount > 1 ? 's' : ''}</>}
            </button>
          ) : (
            <button className="seq-insights-btn-improve" onClick={onAutoImprove} disabled={analyzing || autoImproving}>
              {autoImproving
                ? <><CircularProgress size={11} style={{ color: 'inherit', marginRight: 4 }} /> Improving…</>
                : <><AutoAwesomeIcon style={{ fontSize: 14, marginRight: 4 }} /> Auto-Improve</>}
            </button>
          )
        )}
      </div>
    </Popover>
  );
}
